import { IS_NEW_BACKEND_API, transformArrayToCamelCase } from '@skytech/utils'

import {
  API_ROUTE_LEASING_CUSTOMER_VENDORS,
  API_ROUTE_LEASING_EXTEND,
  API_ROUTE_LEASING_EXTEND_VALIDATE,
  API_ROUTE_LEASING_RETURN,
  API_ROUTE_LEASING_RETURN_VALIDATE,
  API_ROUTE_LEASING_VENDORS,
  API_ROUTE_LEASING_ASSETS_DETAILS,
  API_ROUTE_LEASING_END_OF_LEASE,
  API_ROUTE_LEASING_END_OF_LEASE_VALIDATE,
  API_ROUTE_LEASING_END_OF_LEASE_EMPLOYEE_SELECTION_VALIDATE,
  API_ROUTE_LEASING_SETTINGS,
  API_ROUTE_LEASING_END_OF_LEASE_EMPLOYEE_SELECTION,
} from '@/resources/api-routes'
import { get, post } from '@/util/auth'

import type {
  AssetValidate,
  CustomerVendorListOriginal,
  LeasingAssetDetailsOriginal,
  LeasingSettingsOriginal,
  UpdateEndOfLeasePayload,
} from './types'

export const leasingResources = {
  getCustomerVendors: async () =>
    (await get<CustomerVendorListOriginal>(API_ROUTE_LEASING_VENDORS, IS_NEW_BACKEND_API)).vendors,
  getVendorsByCustomerId: async (customerId: number) => {
    const response = await get<CustomerVendorListOriginal>(
      API_ROUTE_LEASING_CUSTOMER_VENDORS.replace(':id', String(customerId)),
      IS_NEW_BACKEND_API
    )
    return response.vendors
  },
  getLeasingAssetDetails: async (id: number) => {
    const leasingAssetDetailsOriginal = await get<LeasingAssetDetailsOriginal>(
      API_ROUTE_LEASING_ASSETS_DETAILS.replace(':id', String(id)),
      IS_NEW_BACKEND_API
    )

    let parsedLockedFields = leasingAssetDetailsOriginal.lockFields
    if (parsedLockedFields) {
      parsedLockedFields = transformArrayToCamelCase(parsedLockedFields)
    }

    return { lockFields: parsedLockedFields, ...leasingAssetDetailsOriginal }
  },
  getLeasingSettings: async (policyId: number) => {
    const response = await get<LeasingSettingsOriginal>(
      API_ROUTE_LEASING_SETTINGS.replace(':policyId', String(policyId)),
      IS_NEW_BACKEND_API
    )
    if (!response.endOfLease || response.endOfLease?.lockFieldsLeasingSettings.length === 0) {
      return response
    }
    const parsedLockedFields = transformArrayToCamelCase(
      response.endOfLease?.lockFieldsLeasingSettings
    )

    return {
      ...response,
      endOfLease: { ...response.endOfLease, lockFieldsLeasingSettings: parsedLockedFields },
    }
  },

  // Mutations
  endOfLeaseAssets: (payload: { assetIds: number[] }) =>
    post(API_ROUTE_LEASING_END_OF_LEASE, payload, IS_NEW_BACKEND_API),
  endOfLeaseAssetsValidate: (payload: { assetIds: number[] }) =>
    post<AssetValidate[]>(API_ROUTE_LEASING_END_OF_LEASE_VALIDATE, payload, IS_NEW_BACKEND_API),
  endOfLeaseAssetsEmployeeSelection: (payload: { assetIds: number[] }) =>
    post(API_ROUTE_LEASING_END_OF_LEASE_EMPLOYEE_SELECTION, payload, IS_NEW_BACKEND_API),
  endOfLeaseAssetsEmployeeSelectionValidate: (payload: { assetIds: number[] }) =>
    post<AssetValidate[]>(
      API_ROUTE_LEASING_END_OF_LEASE_EMPLOYEE_SELECTION_VALIDATE,
      payload,
      IS_NEW_BACKEND_API
    ),
  extendAssets: (payload: { assetIds: number[]; numberOfMonths: number }) =>
    post(API_ROUTE_LEASING_EXTEND, payload, IS_NEW_BACKEND_API),
  extendAssetsValidate: (payload: { assetIds: number[]; numberOfMonths: number }) =>
    post<AssetValidate[]>(API_ROUTE_LEASING_EXTEND_VALIDATE, payload, IS_NEW_BACKEND_API),
  returnAssets: (payload: { assetIds: number[] }) =>
    post(API_ROUTE_LEASING_RETURN, payload, IS_NEW_BACKEND_API),
  returnAssetsValidate: (payload: { assetIds: number[] }) =>
    post<AssetValidate[]>(API_ROUTE_LEASING_RETURN_VALIDATE, payload, IS_NEW_BACKEND_API),
  updateEndOfLeaseSettings: async (payload: { policyId: number; body: UpdateEndOfLeasePayload }) =>
    await post(
      API_ROUTE_LEASING_SETTINGS.replace(':policyId', String(payload.policyId)),
      payload.body,
      IS_NEW_BACKEND_API
    ),
}

export type CustomerVendor = Awaited<ReturnType<typeof leasingResources.getCustomerVendors>>
export type VendorsByCustomerId = Awaited<
  ReturnType<typeof leasingResources.getVendorsByCustomerId>
>
export type LeasingAssetDetailsReturn = Awaited<
  ReturnType<typeof leasingResources.getLeasingAssetDetails>
>
export type EndOfLeaseWorkflowHistory = Awaited<
  ReturnType<typeof leasingResources.getLeasingAssetDetails>
>['endOfLeaseWorkflowHistory']

export type EndOfLeaseAssetsValidate = Awaited<
  ReturnType<typeof leasingResources.endOfLeaseAssetsValidate>
>

export type EndOfLeaseAssetsEmployeeSelectionValidate = Awaited<
  ReturnType<typeof leasingResources.endOfLeaseAssetsEmployeeSelectionValidate>
>

export type ExtendAssetsValidate = Awaited<ReturnType<typeof leasingResources.extendAssetsValidate>>
export type ReturnAssetsValidate = Awaited<ReturnType<typeof leasingResources.returnAssetsValidate>>
export type LeasingSettings = Awaited<ReturnType<typeof leasingResources.getLeasingSettings>>
